import { Box, Button, Card, styled, TextField, Typography } from '@mui/material';

import { Form, Formik, useFormikContext } from 'formik';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { memo, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import PhoneNumberInput from '../../../components/forms/PhoneNumberInput';
import { ButtonStrings, EventSignUpStrings, FormValidationStrings, UserFormStrings } from '../../../constants';
import { CountryCode } from '../../../types/CountryCode';
import { UserSignUpFormValues } from '../EventSignUp';

interface SignUpFormProps {
  initialValues: Partial<UserSignUpFormValues>;
  onSubmit: (values: Partial<UserSignUpFormValues>) => void;
  submitButtonText?: string;
  onValuesChange?: (values: Partial<UserSignUpFormValues>) => void;
}

const ValuesObserver = ({ onChange }: { onChange?: (values: Partial<UserSignUpFormValues>) => void }) => {
  const { values } = useFormikContext<Partial<UserSignUpFormValues>>();
  const prevValuesRef = useRef<Partial<UserSignUpFormValues> | null>(null);

  useEffect(() => {
    const prev = prevValuesRef.current;
    const next = values;

    const isSame = !!prev && prev.fullname === next.fullname && prev.email === next.email && prev.phone === next.phone;

    if (isSame) {
      return;
    }

    prevValuesRef.current = next;
    onChange?.(next);
  }, [onChange, values]);

  return null;
};

const validationSchema = Yup.object({
  // Core Info
  fullname: Yup.string().required(FormValidationStrings.FULLNAME_REQUIRED),
  phone: Yup.string()
    .required(FormValidationStrings.PHONE_REQUIRED)
    .test('is-valid-phone', FormValidationStrings.INVALID_PHONE, function (value) {
      if (!value) return false;
      const countryCode = this.parent.countryCode || 'US';
      try {
        return isValidPhoneNumber(value, countryCode);
      } catch {
        return false;
      }
    }),
  countryCode: Yup.string(),
  email: Yup.string().trim().email(UserFormStrings.USER_FORM_ERROR_EMAIL_INVALID).required(UserFormStrings.USER_FORM_ERROR_EMAIL_REQUIRED),
});

const SignUpForm = ({ initialValues, onSubmit, submitButtonText = ButtonStrings.BUTTON_CONTINUE, onValuesChange }: SignUpFormProps) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);

  const parseInitialPhone = (phone: string | undefined) => {
    if (!phone) return { phone: '', countryCode: 'US' };

    try {
      const parsed = parsePhoneNumber(phone);
      if (parsed) {
        return {
          phone: parsed.nationalNumber,
          countryCode: parsed.country || selectedCountry?.iso2 || 'US',
        };
      }
    } catch {
      // If parsing fails, return as-is
    }

    return { phone, countryCode: selectedCountry?.iso2 || 'US' };
  };

  const { phone, countryCode } = parseInitialPhone(initialValues.phone);

  const cleanInitialValues = {
    ...initialValues,
    phone,
    countryCode,
  };

  return (
    <StyledCard>
      <Typography variant="h6" style={{ fontWeight: 700, alignSelf: 'center' }}>
        Sign Up
      </Typography>
      <Formik
        enableReinitialize
        initialValues={cleanInitialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={(values) => {
          const cleanPhone = values?.phone?.replace(/\D/g, '');

          const formattedPhone = selectedCountry ? `${selectedCountry.dialCode}${cleanPhone}` : `+${cleanPhone}`;

          onSubmit({
            ...values,
            email: values.email?.trim(),
            phone: formattedPhone,
          });
        }}
      >
        {({ values, handleChange, errors, touched, setFieldValue }) => {
          return (
            <Form>
              <ValuesObserver onChange={onValuesChange} />
              <Box display="grid" gap={3}>
                <FieldContainer>
                  <Label>Full Name</Label>
                  <TextField
                    name="fullname"
                    fullWidth
                    placeholder="Enter your full name"
                    value={values.fullname}
                    onChange={handleChange}
                    error={touched.fullname && !!errors.fullname}
                    helperText={touched.fullname && errors.fullname}
                  />
                </FieldContainer>
                <FieldContainer>
                  <Label>{EventSignUpStrings.PHONE_NUMBER}</Label>
                  <PhoneNumberInput
                    value={values.phone}
                    placeholder="Enter your phone number"
                    onChange={handleChange}
                    error={touched.phone && !!errors.phone}
                    helperText={touched.phone && errors.phone}
                    onCountryChange={(country) => {
                      setSelectedCountry(country);
                      setFieldValue('countryCode', country.iso2);
                    }}
                  />
                </FieldContainer>
                <FieldContainer>
                  <Label>{EventSignUpStrings.EMAIL}</Label>
                  <TextField
                    name="email"
                    type="email"
                    fullWidth
                    placeholder="Enter your email address"
                    value={values.email}
                    onChange={handleChange}
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                </FieldContainer>

                <Button type="submit" variant="contained" color="primary">
                  {submitButtonText}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </StyledCard>
  );
};

const StyledCard = styled(Card)({
  maxWidth: 400,
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  borderRadius: 16,
  alignSelf: 'center',
  margin: '0 auto',
  gap: 16,
});

const FieldContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 12,
});

const Label = styled(Typography)({
  fontWeight: 700,
});

export default memo(SignUpForm);
